<script setup lang="ts">
/**
 * CommandPalette — ⌘K / Ctrl+K global jump-to for the ops app.
 *
 * Mounted once in App.vue (authenticated only). Owns the global open shortcut;
 * open state lives in the shared UI store so the header chip can toggle it too.
 * Sources (pages / records / actions) come from useCommandSources.
 */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUIStore } from '@claimshield/shared'
import { useCommandSources, type Command } from '../../composables/useCommandSources'

const router = useRouter()
const route = useRoute()
const uiStore = useUIStore()

const { query, groups, flatItems, recordsLoading, recordsError, reset } = useCommandSources()

const inputRef = ref<HTMLInputElement>()
const listRef = ref<HTMLElement>()
const activeIndex = ref(0)

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '')

const open = computed({
  get: () => uiStore.commandPaletteOpen,
  set: (v: boolean) => {
    uiStore.commandPaletteOpen = v
  },
})

// Rows flattened for rendering, carrying the running index into flatItems so the
// active-row highlight and keyboard cursor stay in sync across groups.
type RenderRow =
  | { type: 'heading'; key: string; label: string }
  | { type: 'item'; key: string; cmd: Command; index: number }

const renderRows = computed<RenderRow[]>(() => {
  const rows: RenderRow[] = []
  let i = 0
  for (const g of groups.value) {
    rows.push({ type: 'heading', key: `h:${g.heading}`, label: g.heading })
    for (const cmd of g.items) {
      rows.push({ type: 'item', key: cmd.id, cmd, index: i })
      i++
    }
  }
  return rows
})

const hasResults = computed(() => flatItems.value.length > 0)
const showSearching = computed(() => query.value.trim().length >= 2 && recordsLoading.value)

// Reset the cursor to the top whenever the query changes.
watch(query, () => {
  activeIndex.value = 0
})

// Keep the cursor in-bounds if the list shrinks (e.g. records resolve).
watch(flatItems, (items) => {
  if (activeIndex.value > items.length - 1) activeIndex.value = Math.max(0, items.length - 1)
})

watch(open, (isOpen) => {
  if (!isOpen) {
    reset()
    activeIndex.value = 0
  }
})

function onShow() {
  activeIndex.value = 0
  nextTick(() => inputRef.value?.focus())
}

function move(delta: number) {
  const n = flatItems.value.length
  if (!n) return
  activeIndex.value = (activeIndex.value + delta + n) % n
  scrollActiveIntoView()
}

function scrollActiveIntoView() {
  nextTick(() => {
    const el = listRef.value?.querySelector<HTMLElement>(`[data-cmd-index="${activeIndex.value}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  })
}

function activate(cmd?: Command) {
  if (!cmd) return
  if (cmd.kind === 'action') {
    cmd.run?.()
  } else if (cmd.route) {
    if (cmd.route !== route.path) router.push(cmd.route).catch(() => {})
  } else if (cmd.query) {
    router.push({ path: '/', query: cmd.query }).catch(() => {})
  }
  open.value = false
}

function activateCurrent() {
  activate(flatItems.value[activeIndex.value])
}

function onInputKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    move(1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    move(-1)
  } else if (e.key === 'Enter') {
    e.preventDefault()
    activateCurrent()
  }
  // Esc is handled by the dialog (dismissable) + native close.
}

// Global open/close shortcut: ⌘K (mac) / Ctrl+K (win). Modifier combos are
// expected to fire even from inputs, so no editable-target guard here.
function onGlobalKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault()
    uiStore.toggleCommandPalette()
  }
}

onMounted(() => window.addEventListener('keydown', onGlobalKeydown))
onUnmounted(() => window.removeEventListener('keydown', onGlobalKeydown))

const recordBadgeClass: Record<string, string> = {
  Driver: 'bg-blue-100 text-blue-700',
  Applicant: 'bg-amber-100 text-amber-700',
  Client: 'bg-green-100 text-green-700',
}
</script>

<template>
  <PDialog
    v-model:visible="open"
    :modal="true"
    :draggable="false"
    :show-header="false"
    :dismissable-mask="true"
    position="top"
    :pt="{ root: { class: 'cmd-palette-root' }, content: { class: '!p-0 !rounded-xl overflow-hidden' } }"
    @show="onShow"
  >
    <div class="flex flex-col" style="width: min(92vw, 640px)">
      <!-- Search input -->
      <div class="flex items-center gap-3 border-b border-secondary-200 px-4">
        <i class="pi pi-search text-secondary-400" />
        <input
          ref="inputRef"
          v-model="query"
          type="text"
          placeholder="Search pages, clients, drivers, applicants…"
          class="cmd-search-input h-12 flex-1 bg-transparent text-sm text-secondary-800 placeholder-secondary-400 outline-none"
          spellcheck="false"
          autocomplete="off"
          @keydown="onInputKeydown"
        />
        <span v-if="showSearching" class="text-secondary-400"><i class="pi pi-spin pi-spinner" /></span>
      </div>

      <!-- Results -->
      <div ref="listRef" class="max-h-[60vh] overflow-y-auto py-2">
        <template v-for="row in renderRows" :key="row.key">
          <div
            v-if="row.type === 'heading'"
            class="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-secondary-400"
          >
            {{ row.label }}
          </div>
          <button
            v-else
            type="button"
            :data-cmd-index="row.index"
            class="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors"
            :class="row.index === activeIndex ? 'bg-secondary-100' : 'hover:bg-secondary-50'"
            @mousemove="activeIndex = row.index"
            @click="activate(row.cmd)"
          >
            <!-- Record badge -->
            <span
              v-if="row.cmd.kind === 'record'"
              class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
              :class="recordBadgeClass[row.cmd.recordType || ''] || 'bg-secondary-100 text-secondary-600'"
            >
              {{ (row.cmd.recordType || '?')[0] }}
            </span>
            <!-- Page / action icon -->
            <i v-else :class="[row.cmd.icon, 'w-5 flex-shrink-0 text-center text-secondary-500']" />

            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm font-medium text-secondary-800">{{ row.cmd.label }}</span>
              <span v-if="row.cmd.sub" class="block truncate text-xs text-secondary-400">{{ row.cmd.sub }}</span>
            </span>

            <span
              v-if="row.cmd.kind === 'record'"
              class="flex-shrink-0 text-[10px] font-medium uppercase text-secondary-400"
              >{{ row.cmd.recordType }}</span
            >
            <i
              v-else-if="row.cmd.kind === 'action'"
              class="pi pi-bolt flex-shrink-0 text-xs text-secondary-300"
            />
          </button>
        </template>

        <!-- Records error (non-blocking) -->
        <div v-if="recordsError" class="px-4 py-2 text-xs text-secondary-400">
          <i class="pi pi-exclamation-circle mr-1" /> Couldn't load records — pages and actions still work.
        </div>

        <!-- No results -->
        <div v-if="!hasResults && !showSearching" class="px-4 py-8 text-center text-sm text-secondary-400">
          No results for "{{ query.trim() }}"
        </div>
      </div>

      <!-- Footer hint -->
      <div
        class="flex items-center gap-4 border-t border-secondary-200 bg-secondary-50 px-4 py-2 text-[11px] text-secondary-400"
      >
        <span><kbd class="cmd-kbd">↑</kbd><kbd class="cmd-kbd">↓</kbd> navigate</span>
        <span><kbd class="cmd-kbd">↵</kbd> open</span>
        <span><kbd class="cmd-kbd">esc</kbd> close</span>
        <span class="ml-auto"><kbd class="cmd-kbd">{{ isMac ? '⌘' : 'Ctrl' }}</kbd><kbd class="cmd-kbd">K</kbd></span>
      </div>
    </div>
  </PDialog>
</template>

<style scoped>
/* The palette search field sits flush inside the dialog — it must not carry the
   app-wide input focus-ring (a theme('colors.primary.500') box-shadow), which reads as a stray blue
   box here. Focus is always on this input while the palette is open; the active
   result row is the real keyboard-position cue, so no ring is needed. Scoped to
   this input only — the global rule every other form input relies on is untouched. */
.cmd-search-input:focus,
.cmd-search-input:focus-visible {
  outline: none;
  box-shadow: none;
}

.cmd-kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.25rem;
  margin-right: 0.15rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  color: rgb(100 116 139);
  background: #fff;
  border: 1px solid rgb(226 232 240);
  border-radius: 4px;
}
</style>
